/**
 * Client-side image compression using canvas.
 * Reduces phone photos (typically 8-15MB) to fit under the 5MB backend limit
 * without noticeable quality loss for site progress documentation.
 */

const MAX_DIMENSION = 2200;   // px — enough for high-detail site photos
const TARGET_SIZE = 4_500_000; // ~4.5MB target (backend limit is 5MB)
const MIN_QUALITY = 0.55;

function loadImage(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = (e) => { URL.revokeObjectURL(url); reject(e); };
    img.src = url;
  });
}

function canvasToBlob(canvas, type, quality) {
  return new Promise((resolve) => canvas.toBlob(resolve, type, quality));
}

/**
 * Compress an image file until it fits under TARGET_SIZE bytes.
 * Returns a File (JPEG for large photos, original for PNG/WEBP under limit).
 * @param {File} file
 * @returns {Promise<{file: File, ratio: number, originalSize: number, finalSize: number}>}
 */
export async function compressImage(file) {
  const originalSize = file.size;
  const t = (file.type || "").toLowerCase();
  const isHeic = t.includes("heic") || t.includes("heif") || /\.(heic|heif)$/i.test(file.name || "");
  // HEIC can't be decoded by <img> in most browsers — let the backend transcode it.
  if (isHeic) {
    return { file, ratio: 1, originalSize, finalSize: originalSize };
  }
  // Small files stay as-is.
  if (originalSize <= TARGET_SIZE) {
    return { file, ratio: 1, originalSize, finalSize: originalSize };
  }
  let img;
  try { img = await loadImage(file); }
  catch (_e) { return { file, ratio: 1, originalSize, finalSize: originalSize }; }

  // Scale to fit MAX_DIMENSION
  const scale = Math.min(1, MAX_DIMENSION / Math.max(img.width, img.height));
  const w = Math.round(img.width * scale);
  const h = Math.round(img.height * scale);
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (!ctx) return { file, ratio: 1, originalSize, finalSize: originalSize };
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(img, 0, 0, w, h);

  // Force JPEG for compression efficiency on non-transparent photos.
  const outType = file.type === "image/png" && originalSize < 8_000_000 ? "image/png" : "image/jpeg";

  let quality = 0.85;
  let blob = await canvasToBlob(canvas, outType, quality);
  while (blob && blob.size > TARGET_SIZE && quality > MIN_QUALITY) {
    quality -= 0.1;
    blob = await canvasToBlob(canvas, outType, quality);
  }
  if (!blob) return { file, ratio: 1, originalSize, finalSize: originalSize };

  const ext = outType === "image/png" ? "png" : "jpg";
  const baseName = (file.name || "photo").replace(/\.[^.]+$/, "");
  const outFile = new File([blob], `${baseName}.${ext}`, { type: outType, lastModified: Date.now() });
  return {
    file: outFile,
    ratio: outFile.size / originalSize,
    originalSize,
    finalSize: outFile.size,
  };
}
